import React, { useRef, useEffect, useState } from "react";
import { Box } from "@mui/material";
import { motion } from "framer-motion";
import { Search, Filter, Zap, Handshake, Check } from "lucide-react";
import { FormattedMessage, useIntl } from "react-intl";

const TimelineNode = ({ icon: Icon, reached, statBadge }) => (
  <Box
    sx={{
      display: { xs: "none", md: "flex" },
      position: "absolute",
      left: "50%",
      top: "50%",
      transform: "translate(-50%, -50%)",
      zIndex: 10,
      width: "48px",
      height: "48px",
      borderRadius: "9999px",
      alignItems: "center",
      justifyContent: "center",
      transition: "all 0.5s ease",
      bgcolor: reached ? "hsl(145, 63%, 42%)" : "hsl(0, 0%, 100%)",
      border: reached
        ? "2px solid hsl(145, 63%, 42%)"
        : "2px solid hsl(220, 13%, 91%)",
      boxShadow: reached
        ? "0 4px 5px -3px hsla(145, 63%, 42%, 0.3)"
        : "0 4px 5px -1px hsla(145, 63%, 42%, 0.1)",
    }}
  >
    {reached ? (
      <Check size={20} color="hsl(0, 0%, 100%)" />
    ) : (
      <Icon size={20} color="hsl(220, 10%, 46%)" />
    )}

    {statBadge && (
      <Box
        sx={{
          position: "absolute",
          left: "100%",
          ml: "12px",
          top: "50%",
          transform: "translateY(-50%)",
        }}
      >
        <Box
          component={motion.div}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={
            reached
              ? { opacity: 1, scale: 1 }
              : { opacity: 0, scale: 0.8 }
          }
          transition={{ duration: 0.4 }}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            bgcolor: "hsla(145, 63%, 42%, 0.1)",
            border: "1px solid hsla(145, 63%, 42%, 0.2)",
            borderRadius: "9999px",
            px: "16px",
            py: "6px",
            whiteSpace: "nowrap",
          }}
        >
          <Box
            component="span"
            sx={{
              fontSize: "1.125rem",
              fontFamily: "Segoe UI Black, system-ui, sans-serif",
              color: "hsl(145, 63%, 42%)",
            }}
          >
            {statBadge.value}
          </Box>
          <Box
            component="span"
            sx={{
              fontSize: "0.75rem",
              color: "hsl(220, 10%, 46%)",
              fontFamily: "Segoe UI Regular, system-ui, sans-serif",
            }}
          >
            {statBadge.label}
          </Box>
        </Box>
      </Box>
    )}
  </Box>
);

const StepContent = ({ step, index }) => (
  <Box sx={{ display: { xs: "none", md: "block" } }}>
    <Box
      component="span"
      sx={{
        fontSize: "0.75rem",
        fontFamily: "Segoe UI Bold, system-ui, sans-serif",
        color: "hsla(145, 63%, 42%, 0.6)",
        textTransform: "uppercase",
        letterSpacing: "0.1em",
      }}
    >
      {`0${index + 1}`} — {step.label}
    </Box>
    <Box
      component="h3"
      sx={{
        fontSize: "1.25rem",
        fontFamily: "Segoe UI Bold, system-ui, sans-serif",
        color: "hsl(220, 25%, 12%)",
        mt: "8px",
        mb: "12px",
      }}
    >
      {step.title}
    </Box>
    <Box
      component="p"
      sx={{
        color: "hsl(220, 10%, 46%)",
        fontSize: "0.875rem",
        lineHeight: 1.625,
        m: 0,
        fontFamily: "Segoe UI Regular, system-ui, sans-serif",
      }}
    >
      {step.desc}
    </Box>
  </Box>
);

const RecruitmentProcess = () => {
  const intl = useIntl();
  const timelineRef = useRef(null);
  const fillLineRef = useRef(null);
  const nodeRefs = useRef([]);
  const rafRef = useRef(null);
  const [reachedNodes, setReachedNodes] = useState([false, false, false, false]);

  const steps = [
    {
      icon: Search,
      label: intl.formatMessage({ id: "landing_recruiter.recruitment_process.step1_label" }),
      title: intl.formatMessage({ id: "landing_recruiter.recruitment_process.step1_title" }),
      desc: intl.formatMessage({ id: "landing_recruiter.recruitment_process.step1_desc" }, { b: (chunks) => <span style={{ fontFamily: "Segoe UI Semibold, system-ui, sans-serif", color: "hsl(220, 25%, 12%)" }}>{chunks}</span> }),
    },
    {
      icon: Filter,
      label: intl.formatMessage({ id: "landing_recruiter.recruitment_process.step2_label" }),
      title: intl.formatMessage({ id: "landing_recruiter.recruitment_process.step2_title" }),
      desc: intl.formatMessage({ id: "landing_recruiter.recruitment_process.step2_desc" }, { b: (chunks) => <span style={{ fontFamily: "Segoe UI Semibold, system-ui, sans-serif", color: "hsl(220, 25%, 12%)" }}>{chunks}</span> }),
    },
    {
      icon: Zap,
      label: intl.formatMessage({ id: "landing_recruiter.recruitment_process.step3_label" }),
      title: intl.formatMessage({ id: "landing_recruiter.recruitment_process.step3_title" }),
      desc: intl.formatMessage({ id: "landing_recruiter.recruitment_process.step3_desc" }, { b: (chunks) => <span style={{ fontFamily: "Segoe UI Semibold, system-ui, sans-serif", color: "hsl(220, 25%, 12%)" }}>{chunks}</span> }),
    },
    {
      icon: Handshake,
      label: intl.formatMessage({ id: "landing_recruiter.recruitment_process.step4_label" }),
      title: intl.formatMessage({ id: "landing_recruiter.recruitment_process.step4_title" }),
      desc: intl.formatMessage({ id: "landing_recruiter.recruitment_process.step4_desc" }),
    },
  ];

  useEffect(() => {
    const onScroll = () => {
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        if (!timelineRef.current || !fillLineRef.current) return;

        const rect = timelineRef.current.getBoundingClientRect();
        const viewportH = window.innerHeight;
        const startTrigger = viewportH * 0.8;
        const endTrigger = viewportH * 0.4;
        const totalTravel = rect.height + startTrigger - endTrigger;
        const currentTravel = startTrigger - rect.top;
        const progress = Math.min(Math.max(currentTravel / totalTravel, 0), 1);

        fillLineRef.current.style.height = `${progress * 100}%`;

        const containerH = rect.height;
        const fillPx = progress * containerH;

        const newReached = nodeRefs.current.map((ref) => {
          if (!ref) return false;
          const nodeRect = ref.getBoundingClientRect();
          const nodeCenter = nodeRect.top + nodeRect.height / 2 - rect.top;
          return fillPx >= nodeCenter;
        });

        setReachedNodes((prev) => {
          if (prev.every((val, i) => val === newReached[i])) return prev;
          return newReached;
        });
      });
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <Box
      component="section"
      sx={{
        pt: { xs: "32px", md: "48px" },
        pb: { xs: "40px", md: "56px" },
        px: { xs: "16px", sm: "24px", lg: "32px" },
        bgcolor: "hsl(0, 0%, 100%)",
        overflow: "hidden",
      }}
    >
      <Box sx={{ maxWidth: "1024px", mx: "auto" }}>
        {/* Header */}
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          sx={{ textAlign: "center", mb: "64px" }}
        >
          <Box
            component="h2"
            sx={{
              fontSize: { xs: "1.875rem", md: "2.25rem" },
              fontFamily: "Segoe UI Black, system-ui, sans-serif",
              color: "hsl(220, 25%, 12%)",
              letterSpacing: "-0.025em",
              mb: "16px",
              mt: 0,
            }}
          >
            <FormattedMessage id="landing_recruiter.recruitment_process.title" />
          </Box>
          <Box
            component="p"
            sx={{
              color: "hsl(220, 10%, 46%)",
              fontSize: "1.125rem",
              maxWidth: "36rem",
              mx: "auto",
              m: 0,
              fontFamily: "Segoe UI Regular, system-ui, sans-serif",
            }}
          >
            <FormattedMessage id="landing_recruiter.recruitment_process.subtitle" />
          </Box>
        </Box>

        {/* Zigzag Timeline */}
        <Box ref={timelineRef} sx={{ position: "relative" }}>
          {/* Background line — desktop only */}
          <Box
            sx={{
              display: { xs: "none", md: "block" },
              position: "absolute",
              left: "50%",
              transform: "translateX(-0.5px)",
              top: 0,
              bottom: 0,
              width: "2px",
              bgcolor: "hsl(220, 13%, 91%)",
            }}
          />
          {/* Animated fill line — desktop only */}
          <Box
            ref={fillLineRef}
            sx={{
              display: { xs: "none", md: "block" },
              position: "absolute",
              left: "50%",
              transform: "translateX(-0.5px)",
              top: 0,
              width: "2px",
              bgcolor: "hsl(145, 63%, 42%)",
              transformOrigin: "top",
              height: "0%",
            }}
          />

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: { xs: "32px", md: 0 },
            }}
          >
            {steps.map((step, i) => {
              const Icon = step.icon;
              const isLeft = i % 2 === 0;

              return (
                <Box
                  component={motion.div}
                  key={step.label}
                  initial={{ opacity: 0, x: isLeft ? -30 : 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ delay: i * 0.08, duration: 0.5 }}
                  sx={{
                    position: "relative",
                    display: { md: "grid" },
                    gridTemplateColumns: { md: "1fr 1fr" },
                    gap: { md: "48px" },
                    py: { md: "32px" },
                  }}
                >
                  {/* Center node ref wrapper */}
                  <Box
                    ref={(el) => {
                      nodeRefs.current[i] = el;
                    }}
                    sx={{
                      position: "absolute",
                      left: "50%",
                      top: "50%",
                      transform: "translate(-50%, -50%)",
                      width: 0,
                      height: 0,
                      pointerEvents: "none",
                    }}
                  />
                  <TimelineNode
                    icon={Icon}
                    reached={reachedNodes[i]}
                    statBadge={
                      i === 0
                        ? {
                          value: "+776",
                          label: intl.formatMessage({ id: "landing_recruiter.recruitment_process.stat1_label" }),
                        }
                        : i === 1
                          ? {
                            value: "88%",
                            label: intl.formatMessage({ id: "landing_recruiter.recruitment_process.stat2_label" }),
                          }
                          : i === 2
                            ? {
                              value: "15",
                              label: intl.formatMessage({ id: "landing_recruiter.recruitment_process.stat3_label" }),
                            }
                            : i === 3
                              ? {
                                value: "3",
                                label: intl.formatMessage({ id: "landing_recruiter.recruitment_process.stat4_label" }),
                              }
                              : undefined
                    }
                  />

                  {/* Left column */}
                  <Box
                    sx={{
                      ...(isLeft
                        ? { textAlign: { md: "right" }, pr: { md: "64px" } }
                        : { order: { md: 2 }, pl: { md: "64px" } }),
                    }}
                  >
                    {isLeft && <StepContent step={step} index={i} />}
                    {!isLeft && (
                      <Box sx={{ display: { xs: "none", md: "block" } }} />
                    )}
                  </Box>

                  {/* Right column */}
                  <Box
                    sx={{
                      ...(isLeft
                        ? { display: { xs: "none", md: "block" } }
                        : {
                          order: { md: 1 },
                          textAlign: { md: "right" },
                          pr: { md: "64px" },
                        }),
                    }}
                  >
                    {!isLeft && <StepContent step={step} index={i} />}
                  </Box>

                  {/* Mobile layout */}
                  <Box
                    sx={{
                      display: { xs: "flex", md: "none" },
                      gap: "16px",
                    }}
                  >
                    <Box
                      sx={{
                        width: "44px",
                        height: "44px",
                        borderRadius: "9999px",
                        bgcolor: "hsla(145, 63%, 42%, 0.1)",
                        border: "1px solid hsla(145, 63%, 42%, 0.25)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <Icon size={18} color="hsl(145, 63%, 42%)" />
                    </Box>
                    <Box>
                      <Box
                        component="span"
                        sx={{
                          fontSize: "0.75rem",
                          fontFamily:
                            "Segoe UI Bold, system-ui, sans-serif",
                          color: "hsla(145, 63%, 42%, 0.6)",
                          textTransform: "uppercase",
                          letterSpacing: "0.1em",
                        }}
                      >
                        {`0${i + 1}`} — {step.label}
                      </Box>
                      <Box
                        component="h3"
                        sx={{
                          fontSize: "1.125rem",
                          fontFamily:
                            "Segoe UI Bold, system-ui, sans-serif",
                          color: "hsl(220, 25%, 12%)",
                          mt: "4px",
                          mb: "8px",
                        }}
                      >
                        {step.title}
                      </Box>
                      <Box
                        component="p"
                        sx={{
                          color: "hsl(220, 10%, 46%)",
                          fontSize: "0.875rem",
                          lineHeight: 1.625,
                          m: 0,
                          fontFamily:
                            "Segoe UI Regular, system-ui, sans-serif",
                        }}
                      >
                        {step.desc}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* End-of-line message */}
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 10 }}
          animate={
            reachedNodes.every(Boolean)
              ? { opacity: 1, y: 0 }
              : { opacity: 0, y: 10 }
          }
          transition={{ duration: 0.5 }}
          sx={{ mt: "56px", textAlign: "center" }}
        >
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "12px",
              bgcolor: "hsla(145, 63%, 42%, 0.1)",
              border: "1px solid hsla(145, 63%, 42%, 0.2)",
              borderRadius: "9999px",
              px: "24px",
              py: "12px",
            }}
          >
            <Box
              component="span"
              sx={{
                fontSize: { xs: "1.125rem", md: "1.25rem" },
                fontFamily: "Segoe UI Black, system-ui, sans-serif",
                color: "hsl(145, 63%, 42%)",
              }}
            >
              <FormattedMessage id="landing_recruiter.recruitment_process.end_message" />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default RecruitmentProcess;
